import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  LocalShipping,
  CheckCircle,
  Schedule,
  AttachMoney,
  People,
  LocationOn,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format, subDays, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns';
import api from '../services/api';

const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#7c3aed', '#ec4899', '#06b6d4'];

interface StatCard {
  title: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  color: string;
}

const Analytics: React.FC = () => {
  const [period, setPeriod] = useState('7');
  const [loading, setLoading] = useState(true);
  
  // Stats data
  const [overviewStats, setOverviewStats] = useState<any>(null);
  const [dailyTrends, setDailyTrends] = useState<any[]>([]);
  const [driverPerformance, setDriverPerformance] = useState<any[]>([]);
  const [weeklyPerformance, setWeeklyPerformance] = useState<any[]>([]);
  const [completionRates, setCompletionRates] = useState<any[]>([]);
  const [peakHours, setPeakHours] = useState<any[]>([]);
  const [binTypeDistribution, setBinTypeDistribution] = useState<any[]>([]);

  useEffect(() => {
    fetchAllAnalytics();
  }, [period]);

  const fetchAllAnalytics = async () => {
    try {
      setLoading(true);
      
      // Fetch all analytics data from real endpoints
      const [
        overviewRes,
        trendsRes,
        driverPerfRes,
        weeklyRes,
        completionByDayRes,
        peakHoursRes,
        binDistRes,
      ] = await Promise.all([
        api.get('/analytics/overview'),
        api.get(`/analytics/collection-trends?period=${period}`),
        api.get(`/analytics/driver-performance?period=${period}`),
        api.get('/analytics/weekly-performance'),
        api.get(`/analytics/completion-by-day?period=${period}`),
        api.get(`/analytics/peak-hours?period=${period}`),
        api.get(`/analytics/bin-distribution?period=${period}`),
      ]);

      // Process overview stats
      setOverviewStats(overviewRes.data || {});

      // Process daily trends
      const trendsData = trendsRes.data || [];
      const trendData = trendsData.map((item: any) => ({
        date: format(new Date(item.date), 'MMM dd'),
        collections: item.total_collections || 0,
        completed: item.completed || 0,
        revenue: parseFloat(item.revenue || 0),
      }));
      setDailyTrends(trendData);

      // Process driver performance
      const driversData = driverPerfRes.data || [];
      const driverStats = driversData.map((driver: any) => ({
        name: driver.driver_name,
        completed: driver.completed || 0,
        assigned: driver.assigned || 0,
        efficiency: parseInt(driver.efficiency) || 0,
      }));
      setDriverPerformance(driverStats);

      // Process weekly performance
      setWeeklyPerformance(weeklyRes.data || []);

      // Process completion rates by day of week
      setCompletionRates(completionByDayRes.data || []);

      // Process peak hours
      setPeakHours(peakHoursRes.data || []);

      // Process bin type distribution
      setBinTypeDistribution(binDistRes.data || []);

    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard: React.FC<StatCard> = ({ title, value, change, icon, color }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ mb: 1 }}>
              {value}
            </Typography>
            {change !== undefined && (
              <Box display="flex" alignItems="center">
                {change > 0 ? (
                  <TrendingUp sx={{ color: '#10b981', fontSize: 20, mr: 0.5 }} />
                ) : (
                  <TrendingDown sx={{ color: '#ef4444', fontSize: 20, mr: 0.5 }} />
                )}
                <Typography
                  variant="body2"
                  sx={{ color: change > 0 ? '#10b981' : '#ef4444' }}
                >
                  {Math.abs(change)}% from last period
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: color,
              borderRadius: '50%',
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return <Typography>Loading analytics...</Typography>;
  }

  const totalCollections = overviewStats?.month?.total_orders || 0;
  const completedCollections = overviewStats?.month?.completed_orders || 0;
  const completionRate = totalCollections > 0 
    ? Math.round((completedCollections / totalCollections) * 100) 
    : 0;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Analytics Dashboard</Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={period}
            label="Period"
            onChange={(e) => setPeriod(e.target.value)}
          >
            <MenuItem value="7">Last 7 days</MenuItem>
            <MenuItem value="30">Last 30 days</MenuItem>
            <MenuItem value="90">Last 90 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Collections"
            value={totalCollections}
            icon={<LocalShipping sx={{ color: 'white' }} />}
            color="#2563eb"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completion Rate"
            value={`${completionRate}%`}
            icon={<CheckCircle sx={{ color: 'white' }} />}
            color="#10b981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Drivers"
            value={driverPerformance.length}
            icon={<People sx={{ color: 'white' }} />}
            color="#7c3aed"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Monthly Revenue"
            value={`£${parseFloat(String(overviewStats?.month?.total_revenue || 0)).toFixed(2)}`}
            icon={<AttachMoney sx={{ color: 'white' }} />}
            color="#f59e0b"
          />
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Daily Collection Trends
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={dailyTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="collections" 
                  stackId="1"
                  stroke="#2563eb" 
                  fill="#2563eb" 
                  fillOpacity={0.6}
                  name="Total Collections"
                />
                <Area 
                  type="monotone" 
                  dataKey="completed" 
                  stackId="1"
                  stroke="#10b981" 
                  fill="#10b981"
                  fillOpacity={0.6}
                  name="Completed"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Last 7 Days Performance
            </Typography>
            {weeklyPerformance && weeklyPerformance.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={weeklyPerformance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="completed" stackId="a" fill="#10b981" name="Completed" />
                  <Bar dataKey="assigned" stackId="a" fill="#3b82f6" name="Assigned" />
                  <Bar dataKey="pending" stackId="a" fill="#6b7280" name="Pending" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                <Typography color="textSecondary">No data available</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Charts Row 2 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Completion Rates by Day
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={completionRates}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip formatter={(value) => `${value}%`} />
                <Bar dataKey="rate" fill="#10b981" name="Completion Rate">
                  {(completionRates || []).map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.rate >= 90 ? '#10b981' : entry.rate >= 80 ? '#f59e0b' : '#ef4444'} 
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Peak Collection Hours
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={peakHours}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="collections" 
                  stroke="#2563eb" 
                  strokeWidth={2}
                  name="Collections"
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Tables Row */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Driver Performance Rankings
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Driver</TableCell>
                    <TableCell align="right">Completed</TableCell>
                    <TableCell align="right">Assigned</TableCell>
                    <TableCell align="right">Efficiency</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(driverPerformance || [])
                    .sort((a, b) => b.efficiency - a.efficiency)
                    .slice(0, 5)
                    .map((driver, index) => (
                      <TableRow key={index}>
                        <TableCell>{driver.name}</TableCell>
                        <TableCell align="right">{driver.completed}</TableCell>
                        <TableCell align="right">{driver.assigned}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${driver.efficiency}%`}
                            size="small"
                            color={driver.efficiency >= 90 ? 'success' : driver.efficiency >= 75 ? 'warning' : 'error'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Bin Type Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={binTypeDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="type" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" name="Count">
                  {(binTypeDistribution || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Analytics;